import React, { useState, useEffect, useContext, useRef, createContext } from "react";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { AuthContext } from "../Auth/AuthContext";

const TicketsContext = createContext();

const TicketsContextProvider = ({ children }) => {
	const [currentTicket, setCurrentTicket] = useState({ id: null, code: null });
	const [tabOpen, setTabOpen] = useState("open");
	const [pendingCount, setPendingCount] = useState(0);
	const [waitingCount, setWaitingCount] = useState(0);
	const history = useHistory();
	const { user, socket } = useContext(AuthContext);
	const debounceRef = useRef();

	useEffect(() => {
		if (currentTicket.id !== null) {
			history.push(`/tickets/${currentTicket.uuid}`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentTicket])

	// Contagem global de tickets "Em atendimento" (badge do menu) e "IA atendendo"
	// da empresa (card da IA Márcia no cabeçalho). Nada de polling: os valores só são
	// buscados uma vez ao logar e, depois, sempre que o websocket avisar de
	// uma mudança relevante em algum ticket.
	useEffect(() => {
		if (!user?.id || !socket) return undefined;

		const companyId = user.companyId;
		const queueIds = Array.isArray(user.queues) ? user.queues.map(q => q.id) : [];

		const fetchPendingCount = async () => {
			try {
				const { data } = await api.get("/tickets", {
					params: {
						status: "open",
						showAll: "false",
						queueIds: JSON.stringify(queueIds),
					},
				});
				setPendingCount(data.count || 0);
			} catch (err) {
				// contagem é best-effort: falha silenciosa não deve travar a UI
			}
		};

		const fetchWaitingCount = async () => {
			try {
				// Só tickets que a IA está atendendo, sempre da empresa inteira
				// (independe das filas do usuário logado).
				const { data } = await api.get("/ticket/ai-count");
				setWaitingCount(data.count || 0);
			} catch (err) {
				// contagem é best-effort: falha silenciosa não deve travar a UI
			}
		};

		fetchPendingCount();
		fetchWaitingCount();

		const scheduleFetch = () => {
			clearTimeout(debounceRef.current);
			debounceRef.current = setTimeout(() => {
				fetchPendingCount();
				fetchWaitingCount();
			}, 300);
		};

		const onConnect = () => {
			socket.emit("joinNotification");
			scheduleFetch();
		};

		const onCompanyTicket = data => {
			if (data.action === "update" || data.action === "delete") {
				scheduleFetch();
			}
		};

		socket.on("connect", onConnect);
		socket.on(`company-${companyId}-ticket`, onCompanyTicket);

		return () => {
			clearTimeout(debounceRef.current);
			socket.off("connect", onConnect);
			socket.off(`company-${companyId}-ticket`, onCompanyTicket);
		};
	}, [user, socket]);

	return (
		<TicketsContext.Provider
			value={{ currentTicket, setCurrentTicket, tabOpen, setTabOpen, pendingCount, setPendingCount, waitingCount, setWaitingCount }}
		>
			{children}
		</TicketsContext.Provider>
	);
};

export { TicketsContext, TicketsContextProvider };
